import React, { useCallback, useContext, useState } from 'react';
import { signIn } from '@round/api';
import { AuthContext } from '../../AuthContext';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import styles from './SignIn.module.css';
import { Button, FormField, Label } from '@round/ui-kit';
import { Helmet } from 'react-helmet';

type FormValues = {
    email: string;
    password: string;
};

const initialValues: FormValues = {
    email: '',
    password: '',
};

const SignIn = () => {
    const authContext = useContext(AuthContext);
    const [errorSigningIn, setErrorSigningIn] = useState(false);
    const location = useLocation();
    const nextUrl = new URLSearchParams(location.search).get('next') ?? '/';

    const onSubmit = useCallback(
        async ({ email, password }: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
            if (typeof authContext.setTokens !== 'function') {
                return;
            }

            try {
                setErrorSigningIn(false);
                setSubmitting(true);
                const tokens = await signIn(email, password);
                authContext.setTokens(tokens);
            } catch {
                setErrorSigningIn(true);
            } finally {
                setSubmitting(false);
            }
        },
        [authContext]
    );

    if (authContext.loading) {
        return null;
    }

    if (authContext.authenticated) {
        return <Navigate to={nextUrl} replace />;
    }

    return (
        <>
            <Helmet title="Sign in | Apex" />
            <div className={styles.container}>
                <h1 className={styles.heading}>Sign in</h1>
                <Formik initialValues={initialValues} onSubmit={onSubmit}>
                    {({ isSubmitting }) => (
                        <Form>
                            <FormField className={styles.formField}>
                                <Label htmlFor="email">Email</Label>
                                <Field tabIndex={1} id="email" name="email" placeholder="Enter your email" />
                            </FormField>
                            <FormField className={styles.formField}>
                                <Label htmlFor="password">Password</Label>
                                <Field
                                    tabIndex={2}
                                    id="password"
                                    name="password"
                                    type="password"
                                    placeholder="Enter your password"
                                />
                                <Link className={styles.link} to="/forgot-password/">
                                    Forgot Password?
                                </Link>
                            </FormField>

                            {errorSigningIn && <span className={styles.error}>Could not authenticate</span>}

                            <Button
                                tabIndex={3}
                                loading={isSubmitting}
                                className={styles.submit}
                                htmlType="submit"
                                type="filled"
                                color="black"
                            >
                                Sign in
                            </Button>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    );
};

export default SignIn;
